// The browser's session (docs/web-konzept.md, section 5.1). The refresh token lives only in an
// HttpOnly cookie the server sets on /v1/auth; this keeps the short-lived access token in memory and
// renews it shortly before it ends. Tabs refresh one after another (Web Locks): the server signs a
// device out when a refresh token is used twice, and all tabs share the one cookie.

import { api, ApiError } from "../api/client.ts";
import type { SessionResponse, UserResponse } from "../api/contract.gen.ts";
import { deviceName } from "./deviceName.ts";

const TRUSTED_KEY = "voxie.trusted";

/** Renew this long before the access token ends. */
const RENEW_MS = 60_000;

export interface SessionState {
  user: UserResponse;
  accessToken: string;
  /** Epoch ms. */
  expiresAt: number;
}

type Listener = (state: SessionState | null) => void;

export class Session {
  private state: SessionState | null = null;
  private refreshing: Promise<SessionState | null> | null = null;
  private readonly listeners = new Set<Listener>();
  private readonly now: () => number;
  private readonly userAgent: string;
  private readonly locks: LockManager | undefined;

  constructor(options: { now?: () => number; userAgent?: string; locks?: LockManager } = {}) {
    this.now = options.now ?? Date.now;
    this.userAgent = options.userAgent ?? globalThis.navigator?.userAgent ?? "";
    this.locks = options.locks ?? globalThis.navigator?.locks;
  }

  get current(): SessionState | null {
    return this.state;
  }

  /**
   * "Diesem Browser vertrauen" was ticked at sign-in. Kept as a plain flag (no secret) so that
   * Web Push and later the local cache know whether they may stay in this browser.
   */
  get trusted(): boolean {
    try {
      return localStorage.getItem(TRUSTED_KEY) === "1";
    } catch {
      return false;
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Asks for a sign-in code by mail; the dev server answers with the code itself. */
  async requestCode(email: string): Promise<string | undefined> {
    const answer = await api<{ dev_code: string } | undefined>("/v1/auth/code", { method: "POST", json: { email } });
    return answer?.dev_code;
  }

  /** Signs this browser in; [remember] is "Diesem Browser vertrauen". */
  async verify(email: string, code: string, remember: boolean): Promise<UserResponse> {
    const response = await api<SessionResponse>("/v1/auth/verify", {
      method: "POST",
      json: { email, code, device: { platform: "web", name: deviceName(this.userAgent) }, remember },
    });
    try {
      if (remember) localStorage.setItem(TRUSTED_KEY, "1");
      else localStorage.removeItem(TRUSTED_KEY);
    } catch {
      // Storage blocked: the browser counts as not trusted.
    }
    return this.set(response).user;
  }

  /** On start: whether the cookie still holds a session. Null when it does not. */
  async resume(): Promise<SessionState | null> {
    return this.refresh();
  }

  /** A valid access token, renewed if it ends soon; throws ApiError 401 once signed out. */
  async token(): Promise<string> {
    const state = this.state && this.state.expiresAt - this.now() > RENEW_MS ? this.state : await this.refresh();
    if (!state) throw new ApiError(401, "UNAUTHENTICATED", "signed out");
    return state.accessToken;
  }

  /** An authorized call; one 401 renews the token and tries again. */
  async call<T>(path: string, init: Parameters<typeof api>[1] = {}): Promise<T> {
    try {
      return await api<T>(path, { ...init, token: await this.token() });
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) throw e;
      if (this.state) this.state = { ...this.state, expiresAt: 0 };
      return api<T>(path, { ...init, token: await this.token() });
    }
  }

  async logout(): Promise<void> {
    await this.beforeLogout?.().catch(() => undefined);
    try {
      localStorage.removeItem(TRUSTED_KEY);
    } catch {
      // Nothing stored.
    }
    const token = this.state?.accessToken;
    this.set(null);
    if (token) await api("/v1/auth/logout", { method: "POST", token }).catch(() => undefined);
  }

  /** Runs while still signed in, before the server forgets this device (Web Push unsubscribes). */
  beforeLogout: (() => Promise<unknown>) | null = null;

  private refresh(): Promise<SessionState | null> {
    // One renewal at a time in this tab, and across tabs through the lock.
    this.refreshing ??= this.withLock(async () => {
      try {
        return this.set(await api<SessionResponse>("/v1/auth/refresh", { method: "POST", json: {} }));
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 400)) return this.set(null);
        throw e;
      }
    }).finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private withLock<T>(block: () => Promise<T>): Promise<T> {
    return this.locks ? this.locks.request("voxie-session-refresh", block) : block();
  }

  private set(response: SessionResponse): SessionState;
  private set(response: SessionResponse | null): SessionState | null;
  private set(response: SessionResponse | null): SessionState | null {
    this.state = response && { user: response.user, accessToken: response.access_token, expiresAt: Date.parse(response.access_token_expires_at) };
    for (const listener of this.listeners) listener(this.state);
    return this.state;
  }
}

export const session = new Session();
